/**
 * Story 8.12 — Modale profil membre du groupement (lecture seule)
 *
 * Affiche le profil complet d'un cotraitant dans une modale avec :
 * - En-tête : logo, nom, SIRET, représentant, mail, tél, progression
 * - 5 onglets en lecture seule : Présentation, Documents, Références, Bancaire, Équipe
 *
 * Fermable via × ou clic extérieur.
 */
import React, { useState, useMemo, useEffect } from 'react';
import TabNav from '../../../../composants/TabNav/TabNav';
import Button from '../../../../composants/Button/Button';
import { ProfilProgressBar } from './SectionCandidat';
import ReadOnlyPresentation from './readonly/ReadOnlyPresentation';
import ReadOnlyDocuments from './readonly/ReadOnlyDocuments';
import ReadOnlyReferences from './readonly/ReadOnlyReferences';
import ReadOnlyBancaire from './readonly/ReadOnlyBancaire';
import ReadOnlyEquipe from './readonly/ReadOnlyEquipe';
import AvisGroupes from '../../../../composants/AvisPrestataire/AvisGroupes';
import SectionPliable from '../../../../composants/SectionPliable/SectionPliable';
import { AvisData } from '../../../../composants/AvisPrestataire/avisTypes';
import { apiService } from '../../../../services/apiService';
import { useAuth } from '../../../../contexts/AuthContext';
import { isClientRole } from '../../../../utils/roleUtils';
import type { ProfileData } from '../../../../services/profileService';
import './ModalProfilMembre.css';

interface ContactDocument {
    id: number;
    type: string;
    category?: 'accord_cadre' | 'entreprise' | 'commun';
    key?: string;
    commentaire?: string;
    filename?: string;
    updated_at: string;
}

interface ModalProfilMembreProps {
    isOpen: boolean;
    onClose: () => void;
    profile: ProfileData | null;
    isLoading: boolean;
    error: string | null;
    typeBadge?: string | null;
    contactInfo?: { nom: string; prenom: string; email: string; telephone: string; fonction: string; contratNumero?: string | null } | null;
    contactDocuments?: ContactDocument[] | null;
    showCompletion?: boolean;
    showPasserCommande?: boolean;
    onPasserCommande?: () => void;
    /** Bouton "Modifier" — contexte ou le prestataire consulte SON propre profil. Opt-in strict :
     *  absent par defaut, donc l'annuaire cote acheteur est inchange. Le callback recoit le NOM de
     *  l'onglet courant (les onglets de cette modale et ceux de /profil portent les memes noms), pour
     *  ouvrir /profil directement sur l'onglet consulte. */
    showModifier?: boolean;
    onModifier?: (tabName: string) => void;
    /** Onglet ouvert par defaut (0 = Présentation, 1 = Documents, ...). Defaut 0. */
    initialTab?: number;
}

const MEMBER_TABS = ['Présentation', 'Documents', 'Références', 'Bancaire', 'Gestion d\'équipe'];
// Onglet Avis (note du prestataire par les acheteurs) : visible des acheteurs uniquement.
const AVIS_TAB_INDEX = MEMBER_TABS.length;

const ModalProfilMembre: React.FC<ModalProfilMembreProps> = ({
    isOpen,
    onClose,
    profile,
    isLoading,
    error,
    typeBadge,
    contactInfo,
    contactDocuments,
    showCompletion = true,
    showPasserCommande = false,
    onPasserCommande,
    showModifier = false,
    onModifier,
    initialTab = 0,
}) => {
    const [activeTab, setActiveTab] = useState(initialTab);

    // Ouvre la modale sur l'onglet demande (ex. Documents) et y revient a chaque reouverture.
    useEffect(() => {
        if (isOpen) setActiveTab(initialTab);
    }, [isOpen, initialTab]);
    const [showRangTooltip, setShowRangTooltip] = useState(false);
    const [contratPdfUrl, setContratPdfUrl] = useState<string | null>(null);
    const [contratPdfLoading, setContratPdfLoading] = useState(false);

    // Onglet Avis : reserve aux acheteurs (cf. PDF "Seuls les acheteurs peuvent cliquer sur les avis").
    const { user } = useAuth();
    const isAcheteur = isClientRole(user?.role || '');
    const tabs = useMemo(() => (isAcheteur ? [...MEMBER_TABS, 'Évaluations'] : MEMBER_TABS), [isAcheteur]);
    const [avisRecus, setAvisRecus] = useState<AvisData[]>([]);
    const [avisMoyenne, setAvisMoyenne] = useState<number | null>(null);
    const [avisLoaded, setAvisLoaded] = useState(false);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
        };
    }, [isOpen]);

    // Reset des avis quand on change d'entite affichee (le modal est reutilise sans demontage).
    useEffect(() => {
        setAvisLoaded(false);
        setAvisRecus([]);
        setAvisMoyenne(null);
    }, [profile?.entite?.id]);

    // Chargement des avis recus a l'ouverture de l'onglet Avis (une seule fois par entite).
    useEffect(() => {
        if (!isOpen || !isAcheteur || avisLoaded || activeTab !== AVIS_TAB_INDEX) return;
        const entiteId = profile?.entite?.id;
        if (!entiteId) return;
        let cancelled = false;
        apiService.getAvisPrestataireRecus(entiteId)
            .then((res: { avis?: AvisData[]; moyenne?: number | null } | null) => {
                if (cancelled) return;
                setAvisRecus(Array.isArray(res?.avis) ? (res!.avis as AvisData[]) : []);
                setAvisMoyenne(res?.moyenne ?? null);
                setAvisLoaded(true);
            })
            .catch(() => { if (!cancelled) setAvisLoaded(true); });
        return () => { cancelled = true; };
    }, [isOpen, isAcheteur, avisLoaded, activeTab, profile]);

    const contratDoc = useMemo(() => {
        if (!contactDocuments?.length) return null;
        return contactDocuments.find(d => d.category === 'accord_cadre') ?? contactDocuments[0];
    }, [contactDocuments]);

    const handleOpenContrat = async () => {
        if (!contratDoc) return;
        setContratPdfLoading(true);
        try {
            if (contratDoc.binaire) {
                const bytes = Uint8Array.from(atob(contratDoc.binaire), c => c.charCodeAt(0));
                const blob = new Blob([bytes], { type: 'application/pdf' });
                setContratPdfUrl(URL.createObjectURL(blob));
                return;
            }
            let url: string;
            if (contratDoc.type?.startsWith('Contrat')) {
                url = `${process.env.REACT_APP_API_URL}/contrats/${contratDoc.id}/pdf`;
            } else if (contratDoc.category === 'commun') {
                url = `${process.env.REACT_APP_API_URL}/documents-communs/${contratDoc.id}/download`;
            } else if (contratDoc.key) {
                url = `${process.env.REACT_APP_API_URL}/documents/key/${contratDoc.key}/download`;
            } else {
                url = `${process.env.REACT_APP_API_URL}/documents/${contratDoc.id}/download`;
            }
            const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            if (!res.ok) throw new Error('Erreur chargement document');
            const blob = await res.blob();
            setContratPdfUrl(URL.createObjectURL(blob));
        } catch (err) {
            console.error(err);
        } finally {
            setContratPdfLoading(false);
        }
    };

    const closeContratPdf = () => {
        if (contratPdfUrl) URL.revokeObjectURL(contratPdfUrl);
        setContratPdfUrl(null);
    };

    // Pastilles sur onglets incomplets (même logique que ProfilUnifie)
    const tabNotifications = useMemo(() => {
        if (!profile?.completion?.sections) return [];
        const sections = profile.completion.sections;
        const issues: number[] = [];

        if (!sections.presentation) issues.push(0);
        if (!sections.documents) issues.push(1);
        if (sections.references !== undefined && !sections.references) issues.push(2);
        if (!sections.coordonnees || !sections.bancaire) issues.push(3);

        return [...new Set(issues)].map(idx => ({ tabIndex: idx, count: 1 }));
    }, [profile]);

    if (!isOpen) return null;

    return (
        <>
            <div className="modal-backdrop modal-profil-backdrop" onClick={onClose}>
                <div className="modal-profil-membre" onClick={(e) => e.stopPropagation()}>
                    {/* Bouton fermer */}
                    <button className="modal-profil-close" onClick={onClose} aria-label="Fermer">✕</button>

                    {/* Loading */}
                    {isLoading && (
                        <div className="modal-profil-loading">
                            <div className="spinner" />
                            <p>Chargement du profil…</p>
                        </div>
                    )}

                    {/* Erreur */}
                    {error && !isLoading && (
                        <div className="modal-profil-error">
                            <p>{error}</p>
                        </div>
                    )}

                    {/* Contenu profil */}
                    {profile && !isLoading && !error && (
                        <>
                            {/* En-tête profil */}
                            <div className="modal-profil-header">
                                <div className="modal-profil-header-left">
                                    {profile.entite.logo_url ? (
                                        <img
                                            src={profile.entite.logo_url}
                                            alt={`Logo ${profile.entite.nom}`}
                                            className="modal-profil-logo"
                                        />
                                    ) : (
                                        <div className="modal-profil-logo-placeholder">
                                            LOGO
                                        </div>
                                    )}
                                    <div className="modal-profil-identity">
                                        <h2>{profile.entite.nom}</h2>
                                        {typeBadge !== undefined && (typeBadge || profile.entite.suspendu) && (
                                            <div className="modal-profil-header-badges">
                                                {typeBadge && (
                                                    <span className={`modal-profil-badge-type${typeBadge === 'Libre' ? ' modal-profil-badge-type--libre' : ''}`}>
                                                        {typeBadge}
                                                    </span>
                                                )}
                                                {profile.entite.suspendu && (
                                                    <span className="modal-profil-badge-suspendu">
                                                        <svg width="11" height="13" viewBox="0 0 11 13" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                            <rect x="1" y="5.5" width="9" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                                                            <path d="M3 5.5V3.5a2.5 2.5 0 0 1 5 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                        </svg>
                                                        Suspendu
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <div className="modal-profil-details">
                                            {profile.entite.siret && (
                                                <span>SIRET : {profile.entite.siret}</span>
                                            )}
                                            {/* Annuaire : interlocuteur du lot */}
                                            {contactInfo ? (
                                                <>
                                                    {(contactInfo.prenom || contactInfo.nom) && (
                                                        <span>{contactInfo.prenom} {contactInfo.nom}{contactInfo.fonction ? ` – ${contactInfo.fonction}` : ''}</span>
                                                    )}
                                                    {contactInfo.email && (
                                                        <span><a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a></span>
                                                    )}
                                                    {contactInfo.telephone && (
                                                        <span>{formatPhone(contactInfo.telephone)}</span>
                                                    )}
                                                    {contactInfo.contratNumero && (
                                                        contratDoc ? (
                                                            <button
                                                                className="modal-profil-badge-contrat"
                                                                onClick={(e) => { e.stopPropagation(); handleOpenContrat(); }}
                                                                title="Voir le contrat"
                                                                disabled={contratPdfLoading}
                                                            >
                                                                {contratPdfLoading ? 'Chargement…' : `Contrat n° ${contactInfo.contratNumero}`}
                                                            </button>
                                                        ) : (
                                                            <span className="modal-profil-badge-contrat">Contrat n° {contactInfo.contratNumero}</span>
                                                        )
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {profile.coordonnees?.legal_nom && profile.coordonnees?.legal_prenom && (
                                                        <span>Représentant : {profile.coordonnees.legal_prenom} {profile.coordonnees.legal_nom}</span>
                                                    )}
                                                    {profile.coordonnees?.legal_email && (
                                                        <span>Mail : {profile.coordonnees.legal_email}</span>
                                                    )}
                                                    {profile.coordonnees?.legal_tel && (
                                                        <span>Tél : {profile.coordonnees.legal_tel}</span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {showCompletion ? (
                                    <div className="modal-profil-header-right">
                                        <ProfilProgressBar value={profile.entite.progression ?? 0} />
                                    </div>
                                ) : showPasserCommande && (typeBadge === 'Titulaire rang 1' || typeBadge === 'Titulaire rang 2' || typeBadge === 'Titulaire rang 3') ? (
                                    <div className="modal-profil-header-right">
                                        {typeBadge === 'Titulaire rang 1' ? (
                                            <Button
                                                value="Passer Commande"
                                                variant="secondary"
                                                size="small"
                                                onClick={onPasserCommande}
                                            />
                                        ) : (
                                            <div
                                                style={{ position: 'relative', display: 'inline-block', cursor: 'not-allowed' }}
                                                onMouseEnter={() => setShowRangTooltip(true)}
                                                onMouseLeave={() => setShowRangTooltip(false)}
                                            >
                                                <Button
                                                    value="Passer Commande"
                                                    variant="secondary"
                                                    size="small"
                                                    disabled={true}
                                                />
                                                {showRangTooltip && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        bottom: '-100px',
                                                        left: '0',
                                                        transform: 'translateX(-50%)',
                                                        background: '#374151',
                                                        color: '#fff',
                                                        padding: '6px 10px',
                                                        borderRadius: '4px',
                                                        fontSize: '1.2rem',
                                                        lineHeight: '1.4',
                                                        whiteSpace: 'normal',
                                                        width: '260px',
                                                        zIndex: 9999,
                                                        pointerEvents: 'none',
                                                    }}>
                                                        Vous ne pouvez passer commande qu'auprès du titulaire de rang 1 pour commencer. Le rang 2 sera mobilisé uniquement si le rang 1 refuse ou ne répond pas dans les 10 jours. Idem pour le rang 3.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : showModifier ? (
                                    <div className="modal-profil-header-right">
                                        {/* Renvoie vers /profil sur l'onglet actuellement consulte. */}
                                        <Button
                                            value="Modifier"
                                            variant="secondary"
                                            size="small"
                                            onClick={() => onModifier?.(tabs[activeTab])}
                                        />
                                    </div>
                                ) : null}
                            </div>

                            {/* Onglets */}
                            <div className="modal-profil-tabs">
                                <TabNav
                                    tabs={tabs}
                                    activeTab={activeTab}
                                    onTabChange={setActiveTab}
                                    notifications={showCompletion ? tabNotifications : []}
                                />
                            </div>

                            {/* Contenu onglet */}
                            <div className="modal-profil-body">
                                {activeTab === 0 && <ReadOnlyPresentation data={profile} />}
                                {activeTab === 1 && <ReadOnlyDocuments data={profile} />}
                                {activeTab === 2 && <ReadOnlyReferences data={profile} />}
                                {activeTab === 3 && <ReadOnlyBancaire data={profile} />}
                                {activeTab === 4 && <ReadOnlyEquipe data={profile} />}
                                {isAcheteur && activeTab === AVIS_TAB_INDEX && (
                                    <div className="modal-profil-avis">
                                        <SectionPliable title="Avis des acheteurs concernant les prestataires">
                                            <AvisGroupes
                                                avis={avisRecus}
                                                groupBy="acheteur"
                                                average={avisMoyenne}
                                                emptyMessage="Aucun avis pour le moment."
                                                hideOperation
                                            />
                                        </SectionPliable>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Prévisualisation PDF du contrat */}
            {contratPdfUrl && (
                <div className="modal-overlay" onClick={closeContratPdf} style={{ zIndex: 1100 }}>
                    <div className="modal-content-document" onClick={(e) => e.stopPropagation()}>
                        <h3>Contrat n° {contactInfo?.contratNumero}</h3>
                        <iframe
                            src={contratPdfUrl}
                            title={`Contrat n° ${contactInfo?.contratNumero}`}
                            width="100%"
                            height="500px"
                        />
                        <div className="modal-actions">
                            <Button value="Fermer" size="small" variant="secondary" onClick={closeContratPdf} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const formatPhone = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');
    let national = digits;
    if (digits.startsWith('33') && digits.length === 11) national = digits.slice(2);
    else if (digits.startsWith('0') && digits.length === 10) national = digits.slice(1);
    else return phone;
    return `+33 ${national[0]} ${national.slice(1, 3)} ${national.slice(3, 5)} ${national.slice(5, 7)} ${national.slice(7, 9)}`;
};

export default ModalProfilMembre;
